import { AxiosResponse } from "axios";
import axios from "./axios-handler.service";
import { EstadoEntidade } from "@/shared/enumeradores";
import { CanalLead } from "./canal-lead.service";
import { Usuario } from "./usuarios.service";
import { MensagemPronta } from "./mensagem-pronta.service";
import { Paginacao } from "@/shared/interface";
const BASE_URL = "/api/Lead";

export interface Lead {
  estadoEntidade: EstadoEntidade;
  idLead: number;
  idTipoLead: number;
  idEtapaLead: number;
  idStatusLead: number;
  idMotivoLead: number;
  idCanalLead: number;
  canalLead: CanalLead;
  nome: string;
  telefone: number;
  email: string;
  trabalho: string;
  ativo: boolean;
  observacao: string;
  dataCadastro: Date;
  dataUltimaEtapa: Date;
  dataQualificacao: Date;
  dataAgendamento: Date;
  idUsuario: number;
  idConsultor: number;
}

interface PartialPesquisarLeadsCommand {
  nome: string;
  telefone: number;
  idTipoLead: number;
  idCanalLead: number;
  dataInicio: Date;
  dataFim: Date;
  idEtapaLead: number;
  idStatusLead: number;
  idMotivoLead: number;
  idUsuario: number;
}

export interface PesquisarLeadsCommand
  extends Partial<PartialPesquisarLeadsCommand> {
  offset: number;
  limit: number;
  order: string;
}

export interface HistoricoEtapas {
  data: string;
  idEtapaLead: number;
  nomeCampanha: string;
  mensagemPronta: MensagemPronta;
  status: string;
  usuario: Usuario;
}

export interface Filtro {
  offset: number;
  limit: number;
  order?: string;
}

export interface LeadCommand {
  nome: string;
  telefone: number;
  idTipoLead: number;
  idCanalLead: number;
  idEtapaLead: number;
  idStatusLead: number;
  email?: string;
  trabalho?: string;
}

export interface EditarLeadCommand {
  nome: string;
  idTipoLead: number;
  idCanalLead: number;
  email?: string;
  trabalho?: string;
  idUsuario?: number;
}

export class LeadService {
  static async obterPorIdConsultor(idConsultor: number): Promise<Lead> {
    const response = await axios.get<Lead>(
      `${BASE_URL}/obterPorIdConsultor/${idConsultor}`
    );
    return response.data;
  }

  static async pesquisar(
    command: PesquisarLeadsCommand
  ): Promise<Paginacao<Lead>> {
    command.limit = +command.limit || 30;
    const response = await axios.get<Paginacao<Lead>>(`${BASE_URL}/pesquisar`, {
      params: command,
    });
    return response.data;
  }

  static async obterTipoLead(): Promise<any[]> {
    const response = await axios.get<any[]>(`${BASE_URL}/ObterTipoLead`);
    return response.data;
  }

  static async obterLead(
    idLead: number,
    marcarResponsavel?: boolean
  ): Promise<Lead> {
    const response = await axios.get<Lead>(`${BASE_URL}/ObterLead`, {
      params: { idLead, marcarResponsavel },
    });
    return response.data;
  }

  static async listarLeads(idEtapaLead: number): Promise<Lead[]> {
    const response = await axios.get<Lead[]>(`${BASE_URL}/ListarLeads`, {
      params: { idEtapaLead },
    });
    return response.data;
  }

  static async listarQualificados(): Promise<Lead[]> {
    const response = await axios.get<Lead[]>(`${BASE_URL}/ListarQualificados`);
    return response.data;
  }

  static async obterResponsavelLead(idUsuario: number): Promise<string> {
    const response = await axios.get(`${BASE_URL}/ObterResponsavelLead`, {
      params: { idUsuario },
    });
    return response.data;
  }

  static async listarResponsaveis(): Promise<any[]> {
    const response = await axios.get<any[]>(`${BASE_URL}/ListarResponsaveis`);
    return response.data;
  }

  static async listarMotivosLead(): Promise<any[]> {
    const response = await axios.get<any[]>(`${BASE_URL}/ListarMotivosLead`);
    return response.data;
  }

  static async obterMotivoLead(ativos?: boolean): Promise<any[]> {
    const response = await axios.get<any[]>(`${BASE_URL}/ObterMotivosLead`, {
      params: { ativos },
    });
    return response.data;
  }

  static async cadastrarLead(command: LeadCommand): Promise<Lead> {
    const response = await axios.post<Lead>(
      `${BASE_URL}/CadastrarLead`,
      command
    );
    return response.data;
  }

  static async editarLead(
    idLead: number,
    command: EditarLeadCommand
  ): Promise<Lead> {
    const response = await axios.put<Lead>(
      `${BASE_URL}/EditarLead/${idLead}`,
      command
    );
    return response.data;
  }

  static async mudarStatus(
    lead: Lead,
    observacao?: string | null
  ): Promise<void> {
    const { idLead, idStatusLead, idMotivoLead, idConsultor } = lead;

    await axios.post(`${BASE_URL}/MudarStatus`, {
      idLead,
      idStatusLead,
      idMotivoLead,
      idConsultor,
      observacao,
    });
  }

  static async mudarEtapa(
    lead: Lead,
    observacao?: string | null
  ): Promise<void> {
    const { idLead, idEtapaLead, idConsultor } = lead;
    await axios.post(`${BASE_URL}/MudarEtapa`, {
      idLead,
      idEtapaLead,
      idConsultor,
      observacao,
    });
  }

  static async reagendarAtendimento(
    idLead: number,
    data: string,
    hora: string,
    observacao?: string | null
  ): Promise<void> {
    await axios.post(`${BASE_URL}/ReagendarAtendimento`, {
      idLead,
      data,
      hora,
      observacao,
    });
  }

  static async importarLeadsExemplo(fileName: string): Promise<void> {
    return axios
      .get<Blob>(`${BASE_URL}/importarLeadsExemplo`, {
        responseType: "blob",
      })
      .then((res) => this.downLoadResponse(res, fileName));
  }

  static async importarLeads(
    file: File,
    idTipoLead: number,
    idCanalLead: number
  ): Promise<void> {
    const form = new FormData();

    form.append("file", file);
    form.append("idTipoLead", idTipoLead.toString());
    form.append("idCanalLead", idCanalLead.toString());

    return axios
      .post<Blob>(`${BASE_URL}/ImportarLeads`, form, {
        responseType: "blob",
      })
      .then(this.downLoadResponse);
  }

  static async retornoOctadesk(
    file: File,
    nomeCampanha: string,
    idMensagemPronta: number
  ): Promise<void> {
    const form = new FormData();

    form.append("file", file);
    form.append("nomeCampanha", nomeCampanha.toString());
    form.append("idMensagemPronta", idMensagemPronta.toString());

    return axios
      .post<Blob>(`${BASE_URL}/ImportarHistoricoLead`, form, {
        responseType: "blob",
      })
      .then(this.downLoadResponse);
  }

  static async exportarLead(
    dataInicio: Date,
    dataFim: Date,
    idEtapaLead: number,
    idTipoLead: number
  ): Promise<void> {
    return axios
      .post<Blob>(
        `${BASE_URL}/ExportarLead`,
        {
          dataInicio,
          dataFim,
          idEtapaLead,
          idTipoLead,
        },
        {
          responseType: "blob",
        }
      )
      .then(this.downLoadResponse);
  }

  static exportarPesquisaLead(command: PesquisarLeadsCommand): Promise<void> {
    return axios
      .post(`${BASE_URL}/exportarPesquisaLead`, command, {
        responseType: "blob",
      })
      .then(this.downLoadResponse);
  }

  static exportarPesquisaLeadOcta(
    command: PesquisarLeadsCommand
  ): Promise<void> {
    return axios
      .post(`${BASE_URL}/exportarPesquisaLeadOcta`, command, {
        responseType: "blob",
      })
      .then(this.downLoadResponse);
  }

  static async listarHistoricoEtapas(
    idLead: number,
    filtro: Filtro
  ): Promise<Paginacao<HistoricoEtapas>> {
    const { offset, limit, order } = filtro;
    const response = await axios.get<Paginacao<HistoricoEtapas>>(
      `${BASE_URL}/ListarHistoricoEtapas`,
      {
        params: {
          idLead,
          offset,
          limit,
          order,
        },
      }
    );
    return response.data;
  }

  private static async downLoadResponse(
    response: AxiosResponse<Blob>,
    fileNameDefault = "default.file"
  ): Promise<void> {
    const href = window.URL.createObjectURL(response.data);

    const anchorElement = document.createElement("a");
    anchorElement.href = href;
    anchorElement.style.display = "none";

    const contentDisposition = response.headers["content-disposition"];

    let fileName = fileNameDefault;

    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
      if (fileNameMatch?.length === 2) {
        fileName = fileNameMatch[1];
      }
    }

    anchorElement.download = fileName;

    document.body.appendChild(anchorElement);
    anchorElement.click();

    document.body.removeChild(anchorElement);
    window.URL.revokeObjectURL(href);
  }
}
